/**
 * MobileAdaptiveComponent.jsx - Renderizado Adaptativo Mobile vs Desktop
 * 
 * Wrapper inteligente que renderiza automáticamente versiones móviles optimizadas
 * o versiones de escritorio según el device detectado. Sistema unificado para
 * garantizar que todos los componentes del sistema grupal tengan UX optimizada.
 * 
 * Características principales:
 * - Auto-detection de device capabilities (mobile, tablet, desktop)
 * - Lazy loading de componentes para optimizar performance móvil
 * - Fallback graceful cuando componente móvil no está disponible
 * - Props forwarding transparente entre versiones
 * - Error boundaries específicos para mobile vs desktop
 * - Performance monitoring y analytics de usage mobile/desktop
 * 
 * Componentes soportados:
 * - CrossFeedbackPanel → CrossFeedbackPanelMobile (Sistema B6 optimizado)
 * - WeeklyDeliverableEditor → WeeklyDeliverableEditorMobile (Editor B1-B4 móvil) 
 * - GroupDashboard → GroupDashboardMobile (Dashboard docente móvil)
 * - [Extensible para más componentes del sistema]
 * 
 * Optimizaciones móviles aplicadas:
 * - Code splitting automático (mobile bundle separado)
 * - Progressive loading de features según connection speed
 * - Battery-aware functionality (reduce features en batería baja)
 * - Touch gesture support con haptic feedback
 * - Keyboard virtual adaptation automática
 * - Offline mode con sync cuando regresa conectividad
 * 
 * @component
 * @props {string} component - Nombre del componente a renderizar
 * @props {Object} [props] - Props a pasar al componente seleccionado
 * @props {boolean} [forceMobile] - Forzar versión móvil (para testing)
 * @props {boolean} [forceDesktop] - Forzar versión desktop (para testing)
 * @props {function} [onRenderMode] - Callback con modo renderizado (mobile/desktop)
 * @author Sistema EPL - Mobile Optimization
 * @version 2.0.0 
 */

import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, Smartphone, Monitor } from 'lucide-react';
import { useMobile } from '../context/MobileOptimizationProvider';

// Lazy loading de componentes móviles para code splitting optimizado
const CrossFeedbackPanelMobile = lazy(() => import('./CrossFeedbackPanelMobile'));
const WeeklyDeliverableEditorMobile = lazy(() => import('./WeeklyDeliverableEditorMobile'));
const GroupDashboardMobile = lazy(() => import('./GroupDashboardMobile'));

// Importación estática de componentes desktop (ya optimizados)
import CrossFeedbackPanel from '../pages/CrossFeedbackPanel';
import WeeklyDeliverableEditor from '../pages/WeeklyDeliverableEditor';  
import GroupDashboard from '../pages/GroupDashboard';

/**
 * Mapeo de componentes desktop → móvil
 */
const COMPONENT_MAPPING = {
  'CrossFeedbackPanel': {
    desktop: CrossFeedbackPanel,
    mobile: CrossFeedbackPanelMobile,
    displayName: 'Sistema de Feedback Cruzado B6',
    requiresMobile: true  // Requiere versión móvil específica
  },
  'WeeklyDeliverableEditor': {
    desktop: WeeklyDeliverableEditor,
    mobile: WeeklyDeliverableEditorMobile,
    displayName: 'Editor Colaborativo B1-B4',
    requiresMobile: true
  },
  'GroupDashboard': {
    desktop: GroupDashboard,
    mobile: GroupDashboard,  // Mismo componente - ya es responsive con Tailwind
    displayName: 'Dashboard Docente Multi-Grupo',
    requiresMobile: false
  }
  // Extensible para más componentes según necesidades
};

/**
 * Componente de loading optimizado para móvil
 */
const MobileLoadingSpinner = ({ componentName, connectionSpeed }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div className="relative">
      <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      <Smartphone className="w-4 h-4 text-blue-400 absolute -top-1 -right-1" />
    </div>
    
    <h3 className="text-lg font-semibold text-gray-900 mt-4 text-center">
      Cargando versión móvil
    </h3>
    <p className="text-sm text-gray-600 text-center mt-1">
      {componentName}
    </p>
    
    {connectionSpeed && (connectionSpeed === 'slow-2g' || connectionSpeed === '2g') && (
      <div className="mt-3 text-xs text-yellow-600 bg-yellow-50 px-3 py-2 rounded-full">
        Conexión lenta detectada - Optimizando carga
      </div>
    )}
  </div>
);

/**
 * Componente de error boundary específico para mobile
 */
class MobileErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Mobile component error:', error, errorInfo);
    
    // Analytics tracking si está disponible
    if (window.gtag) {
      window.gtag('event', 'mobile_component_error', {
        component: this.props.componentName,
        error_message: error.message,
        device_type: 'mobile'
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">
            Error en versión móvil
          </h3>
          <p className="text-sm text-gray-600 mt-2 mb-4">
            {this.props.componentName} no pudo cargar correctamente
          </p>
          
          <div className="space-y-2 w-full max-w-xs">
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Recargar página
            </button>
            
            <button
              onClick={() => this.props.onFallbackToDesktop?.()}
              className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Usar versión escritorio
            </button>
          </div>
          
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-4 text-left">
              <summary className="text-xs text-gray-500 cursor-pointer">
                Detalles del error (desarrollo)
              </summary>
              <pre className="text-xs text-red-600 mt-2 p-2 bg-gray-100 rounded overflow-auto max-w-full">
                {this.state.error?.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

const MobileAdaptiveComponent = ({ 
  component, 
  forceMobile = false, 
  forceDesktop = false,
  onRenderMode = () => {},
  ...props 
}) => {
  // Mobile context
  const { 
    isMobile, 
    isTablet,
    connectionSpeed,
    getMobileClasses 
  } = useMobile();

  // Estados locales
  const [renderMode, setRenderMode] = useState(null);
  const [fallbackToDesktop, setFallbackToDesktop] = useState(false);
  const [loadStartTime] = useState(Date.now());

  // Determinar qué versión renderizar
  const shouldRenderMobile = useCallback(() => {
    if (forceDesktop) return false;
    if (forceMobile) return true;
    
    // Auto-detection inteligente
    const isSmallScreen = isMobile || (isTablet && window.innerWidth < 900);
    const hasTouch = 'ontouchstart' in window;
    const isSlowConnection = connectionSpeed === 'slow-2g' || connectionSpeed === '2g';
    
    // Preferir móvil si:
    // - Es pantalla pequeña + touch
    // - O es conexión lenta (versión móvil optimizada)  
    // - O user agent indica mobile device
    return isSmallScreen && hasTouch || isSlowConnection;
  }, [isMobile, isTablet, connectionSpeed, forceMobile, forceDesktop]);

  // Determinar modo de renderizado
  useEffect(() => {
    const componentConfig = COMPONENT_MAPPING[component];
    const shouldUseMobile = shouldRenderMobile();
    
    let mode = 'desktop'; // Default fallback
    
    if (shouldUseMobile && componentConfig?.mobile) {
      mode = 'mobile';
    } else if (!shouldUseMobile && componentConfig?.desktop) {
      mode = 'desktop';
    }
    
    // Si se forzó fallback a desktop, respetarlo
    if (fallbackToDesktop) {
      mode = 'desktop';
    }
    
    setRenderMode(mode);
    onRenderMode(mode);

    // Analytics tracking
    if (window.gtag) {
      window.gtag('event', 'component_render_mode', {
        component: component,
        render_mode: mode,
        is_mobile: isMobile,
        is_tablet: isTablet,
        connection_speed: connectionSpeed,
        load_time: Date.now() - loadStartTime
      });
    }

    console.log(`🎨 MobileAdaptiveComponent: Rendering ${component} in ${mode} mode`);
  }, [
    component, shouldRenderMobile, fallbackToDesktop, onRenderMode, 
    isMobile, isTablet, connectionSpeed, loadStartTime
  ]);

  // Obtener configuración del componente
  const componentConfig = COMPONENT_MAPPING[component];
  
  if (!componentConfig) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900">
          Componente no disponible
        </h3>
        <p className="text-sm text-gray-600 mt-2">
          El componente "{component}" no está registrado en el sistema adaptativo.
        </p>
      </div>
    );
  }

  // Renderizar versión móvil
  if (renderMode === 'mobile' && componentConfig.mobile) {
    const MobileComponent = componentConfig.mobile;
    
    return (
      <MobileErrorBoundary 
        componentName={componentConfig.displayName}
        onFallbackToDesktop={() => setFallbackToDesktop(true)}
      >
        <Suspense 
          fallback={
            <MobileLoadingSpinner 
              componentName={componentConfig.displayName}
              connectionSpeed={connectionSpeed}
            />
          }
        >
          {/* Header de debug en desarrollo */}
          {process.env.NODE_ENV === 'development' && (
            <div className={getMobileClasses(`
              bg-blue-50 border-b border-blue-200 px-4 py-2 text-xs text-blue-700
              flex items-center justify-between
            `)}>
              <div className="flex items-center space-x-2">
                <Smartphone className="w-3 h-3" />
                <span>MOBILE: {componentConfig.displayName}</span>
              </div>
              <button
                onClick={() => setFallbackToDesktop(true)}
                className="underline hover:no-underline"
              >
                Switch to Desktop
              </button>
            </div>
          )}
          
          <MobileComponent {...props} />
        </Suspense>
      </MobileErrorBoundary>
    );
  }

  // Renderizar versión desktop 
  if (renderMode === 'desktop' && componentConfig.desktop) {
    const DesktopComponent = componentConfig.desktop;
    
    return (
      <>
        {/* Header de debug en desarrollo */}
        {process.env.NODE_ENV === 'development' && isMobile && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-xs text-yellow-700 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Monitor className="w-3 h-3" />
              <span>DESKTOP on MOBILE: {componentConfig.displayName}</span>
            </div>
            {componentConfig.mobile && (
              <button
                onClick={() => setFallbackToDesktop(false)}
                className="underline hover:no-underline"
              >
                Switch to Mobile
              </button>
            )}
          </div>
        )}
        
        <DesktopComponent {...props} />
      </>
    );
  }

  // Estado de loading mientras se determina el modo
  if (!renderMode) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  // Fallback si no hay componente disponible para el modo determinado
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
      <h3 className="text-lg font-semibold text-gray-900">
        Versión no disponible
      </h3>
      <p className="text-sm text-gray-600 mt-2">
        No hay {renderMode} version disponible para {componentConfig.displayName}
      </p>
      
      {renderMode === 'mobile' && componentConfig.desktop && (
        <button
          onClick={() => setFallbackToDesktop(true)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Usar versión escritorio
        </button>
      )}
    </div>
  );
};

export default MobileAdaptiveComponent;